/**
 * PATH: packages/email-hub/src/feature/components/rich-text/snippetStore.ts
 * FILE_ID: EMAILHUB-RICHTEXT-SNIPPETSTORE
 * PURPOSE: Persistent store for reusable outbound compose snippets ("templates")
 *          — saved HTML blocks the operator can re-insert via the "/" menu.
 * WHY: Compose had no reusable-block insert (Kanban templates exist, outbound
 *      didn't). Operators retype the same intros/closings; this lets them save
 *      a block once and drop it in anywhere. FE-only (localStorage), tenant-
 *      scoped, with all mutation as pure I→O so it's unit-testable.
 * FLOW: load(key) → ComposeSnippet[]; upsert/remove return a NEW array; save(key)
 *       persists. createSnippet validates + stamps id/updatedAt.
 * DEPENDENCIES: none (storage + clock are injectable; default to globals).
 * RELATED:
 *   - snippetSlash.ts: maps snippets → "/" menu items for insertion.
 *   - SnippetManagerModal.tsx: save + delete UI.
 */

export interface ComposeSnippet {
  id: string
  title: string
  /** Sanitised HTML fragment inserted at the cursor. */
  html: string
  updatedAt: number
}

export const MAX_SNIPPETS = 50
export const SNIPPET_TITLE_MAX = 80

const KEY_PREFIX = 'quotech.composeSnippets'

/** Tenant/account-scoped storage key so snippets don't leak across logins. */
export function snippetStorageKey(scope?: string | null): string {
  const s = (scope || '').trim()
  return s ? `${KEY_PREFIX}.${s}` : KEY_PREFIX
}

/** Trim + collapse whitespace + clamp to the title ceiling. */
export function normaliseTitle(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim().slice(0, SNIPPET_TITLE_MAX)
}

function isSnippet(v: unknown): v is ComposeSnippet {
  if (!v || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  return (
    typeof o.id === 'string' &&
    typeof o.title === 'string' &&
    typeof o.html === 'string' &&
    typeof o.updatedAt === 'number'
  )
}

/** Newest first — the order the "/" menu and manager list show them. */
function byNewest(a: ComposeSnippet, b: ComposeSnippet): number {
  return b.updatedAt - a.updatedAt
}

/**
 * Parse + validate the persisted blob. Bad/old shapes are dropped rather than
 * thrown so a corrupt entry can never brick the composer.
 */
export function parseSnippets(raw: string | null): ComposeSnippet[] {
  if (!raw) return []
  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(data)) return []
  return data.filter(isSnippet).slice(0, MAX_SNIPPETS).sort(byNewest)
}

export function loadSnippets(
  key: string,
  storage?: Pick<Storage, 'getItem'>,
): ComposeSnippet[] {
  const store = storage ?? safeLocalStorage()
  if (!store) return []
  try {
    return parseSnippets(store.getItem(key))
  } catch {
    return []
  }
}

export function saveSnippets(
  key: string,
  list: ComposeSnippet[],
  storage?: Pick<Storage, 'setItem'>,
): void {
  const store = storage ?? safeLocalStorage()
  if (!store) return
  try {
    store.setItem(key, JSON.stringify(list.slice(0, MAX_SNIPPETS)))
  } catch {
    // Quota / private-mode — snippets are a convenience, never block compose.
  }
}

export interface CreateSnippetInput {
  title: string
  html: string
}

export interface CreateSnippetDeps {
  now?: () => number
  randomId?: () => string
}

function defaultId(): string {
  // crypto.randomUUID where available; fall back to a timestamp+random id.
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c?.randomUUID) return c.randomUUID()
  return `snip_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

/**
 * Validate + stamp a new snippet. Returns null when the title is blank or the
 * body has no real content (whitespace/empty tags only) — the caller surfaces
 * the reason; we never persist an empty template.
 */
export function createSnippet(
  input: CreateSnippetInput,
  deps?: CreateSnippetDeps,
): ComposeSnippet | null {
  const title = normaliseTitle(input.title)
  const html = (input.html || '').trim()
  if (!title) return null
  if (!htmlHasContent(html)) return null
  return {
    id: deps?.randomId ? deps.randomId() : defaultId(),
    title,
    html,
    updatedAt: deps?.now ? deps.now() : Date.now(),
  }
}

/** True when the HTML carries text or a media/break element worth saving. */
export function htmlHasContent(html: string): boolean {
  if (!html) return false
  if (/<(img|table|hr|br)[\s>/]/i.test(html)) return true
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return text.length > 0
}

/**
 * Insert or replace by id, newest first, capped at MAX_SNIPPETS. Pure — returns
 * a new array so React state updates stay referentially honest.
 */
export function upsertSnippet(
  list: ComposeSnippet[],
  snippet: ComposeSnippet,
): ComposeSnippet[] {
  const without = list.filter((s) => s.id !== snippet.id)
  return [snippet, ...without].sort(byNewest).slice(0, MAX_SNIPPETS)
}

export function removeSnippet(
  list: ComposeSnippet[],
  id: string,
): ComposeSnippet[] {
  return list.filter((s) => s.id !== id)
}

function safeLocalStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}
